/**
 * HighlightedTranscript
 * ──────────────────────
 * Renders transcript text, marking char-offset spans that matched a tagged
 * Auto Sync reference document (see reference_match.py on the backend) like
 * citations in a textbook: an underlined passage with a numbered marker, and
 * the matching line from the reference listed as a footnote below. With no
 * spans it renders identically to a plain whitespace-pre-wrap paragraph — no
 * visual change for sessions without a tagged reference.
 */
import React from 'react';

const DEFAULT_CLASS = 'text-sm text-slate-700 leading-relaxed whitespace-pre-wrap';

// Low ink band + solid underline; `clone` keeps both on every line of a wrapped passage.
const MATCH_STYLE = {
  backgroundImage: 'linear-gradient(transparent 52%, rgba(16,185,129,0.24) 52%)',
  boxShadow: 'inset 0 -1.5px 0 #10b981',
  WebkitBoxDecorationBreak: 'clone',
  boxDecorationBreak: 'clone',
};

export default function HighlightedTranscript({ text, spans, className, sourceTitle }) {
  const safeText = text || '';
  const clean = (spans || [])
    .filter(s => Number.isFinite(s?.start) && Number.isFinite(s?.end) && s.end > s.start)
    .sort((a, b) => a.start - b.start);

  // Defensive clip against overlaps — keep the first (highest-scoring pass
  // already deduped upstream) and drop anything that starts before the
  // previous span ended.
  const merged = [];
  let cursor = 0;
  for (const s of clean) {
    if (s.start < cursor) continue;
    merged.push(s);
    cursor = s.end;
  }

  if (merged.length === 0) {
    return (
      <p className={className || DEFAULT_CLASS} data-testid="highlighted-transcript">
        {safeText}
      </p>
    );
  }

  const nodes = [];
  let pos = 0;
  merged.forEach((s, i) => {
    if (s.start > pos) nodes.push(<React.Fragment key={`t-${i}`}>{safeText.slice(pos, s.start)}</React.Fragment>);
    nodes.push(
      <mark key={`m-${i}`} className="bg-transparent text-green-950" style={MATCH_STYLE} data-testid={`transcript-match-${i}`}>
        {safeText.slice(s.start, s.end)}
      </mark>,
      <sup
        key={`n-${i}`}
        className="ml-0.5 inline-flex items-center justify-center min-w-[15px] h-[15px] px-1 rounded-full bg-green-800 text-white text-xs font-semibold leading-none align-super"
        aria-label={`Reference match ${i + 1}`}>
        {i + 1}
      </sup>
    );
    pos = s.end;
  });
  if (pos < safeText.length) nodes.push(<React.Fragment key="t-end">{safeText.slice(pos)}</React.Fragment>);

  return (
    <div>
      <p className={className || DEFAULT_CLASS} data-testid="highlighted-transcript">
        {nodes}
      </p>
      <ol className="mt-4 pt-3 border-t border-green-100 space-y-1.5" data-testid="transcript-match-notes">
        {merged.map((s, i) => (
          <li key={i} className="flex gap-2 text-xs leading-snug text-slate-600">
            <span className="shrink-0 mt-px inline-flex items-center justify-center min-w-[15px] h-[15px] px-1 rounded-full bg-green-800 text-white text-xs font-semibold leading-none">
              {i + 1}
            </span>
            <span>
              {s.excerpt
                ? <span className="font-serif italic text-slate-700">“{s.excerpt}”</span>
                : 'Matches the tagged reference.'}
              {sourceTitle && <span className="text-slate-400"> — {sourceTitle}</span>}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}
